import type { ProductPlan } from "../types"

export type PlanParams = Record<string, string | undefined>

/** Read a single plan id from a param (e.g. `?plan=foo`). */
export function getPlanId(params: PlanParams, key: string): string | null {
  const value = params[key]
  return value && value.trim() ? value.trim() : null
}

/** Read a CSV list of plan ids from a param (e.g. `?plans=a,b,c`). */
export function getPlanIds(params: PlanParams, key: string): string[] {
  const value = params[key]
  if (!value) return []
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
}

/**
 * Collect every plan id referenced by the URL params so they can be fetched
 * in a single batch request. Extend `SINGLE_PLAN_PARAMS` to support more offers.
 */
const SINGLE_PLAN_PARAMS = ["plan"] as const
const LIST_PLAN_PARAMS = ["plans"] as const

export function buildRequestedPlanIds(params: PlanParams): string[] {
  const ids = new Set<string>()

  for (const key of LIST_PLAN_PARAMS) {
    getPlanIds(params, key).forEach((id) => ids.add(id))
  }
  for (const key of SINGLE_PLAN_PARAMS) {
    const id = getPlanId(params, key)
    if (id) ids.add(id)
  }

  return Array.from(ids)
}

/** True when every plan referenced by the params is present in `plans`. */
export function arePlansReady(params: PlanParams, plans: ProductPlan[]): boolean {
  const requested = buildRequestedPlanIds(params)
  if (requested.length === 0) return false
  return requested.every((id) => plans.some((plan) => plan.id === id))
}

/**
 * Resolve the list of plans to display, in the order requested by the params.
 * Falls back to the single `plan` param, then to all provided plans.
 */
export function resolveVisiblePlans(params: PlanParams, plans: ProductPlan[]): ProductPlan[] {
  const byId = new Map(plans.map((plan) => [plan.id, plan]))

  const listIds = LIST_PLAN_PARAMS.flatMap((key) => getPlanIds(params, key))
  if (listIds.length > 0) {
    const resolved = listIds.map((id) => byId.get(id)).filter(Boolean) as ProductPlan[]
    if (resolved.length > 0) return resolved
  }

  const single = getPlanId(params, "plan")
  if (single && byId.has(single)) return [byId.get(single) as ProductPlan]

  return plans
}

/**
 * Pick the plan that should be pre-selected.
 * Priority: explicit `plan` param → badged plan → middle of the list → first.
 */
export function resolveSelectedPlan(params: PlanParams, plans: ProductPlan[]): ProductPlan | null {
  if (plans.length === 0) return null

  const single = getPlanId(params, "plan")
  if (single) {
    const match = plans.find((plan) => plan.id === single)
    if (match) return match
  }

  const visible = resolveVisiblePlans(params, plans)
  const badged = visible.find((plan) => plan.badge)
  if (badged) return badged

  return visible[Math.floor(visible.length / 2)] ?? visible[0] ?? null
}
